import { useCallback, useEffect, useState } from 'react';
import { useSupabase } from './useSupabase';
import { toast } from '@/components/ui';
import type { OrphanCrewNumber } from '@/types/orphanCrewNumber';

interface UseOrphanCrewNumbersReturn {
  orphans: OrphanCrewNumber[];
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  releaseOrphanCrewNumber: (crewId: string, reason: string) => Promise<void>;
}

/**
 * Lecture de crew_numbers_orphans_view (tri âge décroissant : plus anciens
 * d'abord) + action release_orphan_crew_number. TICKET-CRM-07.
 */
export function useOrphanCrewNumbers(): UseOrphanCrewNumbersReturn {
  const supabase = useSupabase();
  const [orphans, setOrphans] = useState<OrphanCrewNumber[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchOrphans = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: fetchError } = await supabase
      .from('crew_numbers_orphans_view')
      .select('*')
      .order('age_days', { ascending: false });

    if (fetchError) {
      setError(new Error(fetchError.message));
      setOrphans([]);
      // Toast en plus de l'ErrorState : couvre le cas d'un échec sur refetch
      // (après libération), où la page a déjà passé le bloc d'erreur initial
      // (revue 2026-06-01, finding C).
      toast.error('Erreur lors du chargement des numéros orphelins', {
        description: fetchError.message,
      });
    } else {
      setOrphans((data as unknown as OrphanCrewNumber[]) || []);
    }
    setLoading(false);
  }, [supabase]);

  useEffect(() => {
    fetchOrphans();
  }, [fetchOrphans]);

  const releaseOrphanCrewNumber = useCallback(
    async (crewId: string, reason: string): Promise<void> => {
      const { error: rpcError } = await supabase.rpc('release_orphan_crew_number', {
        p_crew_id: crewId,
        p_reason: reason,
      });
      if (rpcError) {
        // Le message porte le code métier (access_denied, crew_has_paid_payment, ...)
        throw new Error(rpcError.message);
      }
      await fetchOrphans();
    },
    [supabase, fetchOrphans]
  );

  return { orphans, loading, error, refresh: fetchOrphans, releaseOrphanCrewNumber };
}
